import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getAllUsers } from "../../services/adminRechargeService";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../database/firebase";
import {
  Search,
  RefreshCw,
  ExternalLink,
  Pencil,
  User,
  Phone,
  ShoppingBag,
  CheckCircle,
  X,
  Package,
  CalendarDays,
  FileDown,
  ChevronDown,
} from "lucide-react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Shake packages matching AdminRecharge.jsx
const SHAKE_PACKAGES = [
  { name: "Starter", days: 1, totalPrice: 240, pricePerDay: 240 },
  { name: "Growth", days: 5, totalPrice: 1150, pricePerDay: 230 },
  { name: "Advanced", days: 15, totalPrice: 3300, pricePerDay: 220 },
  { name: "Ultimate", days: 25, totalPrice: 5250, pricePerDay: 210 },
];

const NoTransactionList = () => {
  const navigate = useNavigate();
  const [allUsers, setAllUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [dateFilter, setDateFilter] = useState("All");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedReferrer, setSelectedReferrer] = useState("All");
  const [selectedSponsor, setSelectedSponsor] = useState("All");

  // Modal state
  const [editingUser, setEditingUser] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Package form state
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [validityDate, setValidityDate] = useState("");

  const loadData = async () => {
    setIsLoading(true);
    try {
      const users = await getAllUsers();
      setAllUsers(users);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Reset form when modal opens
  const openEditModal = (u) => {
    setEditingUser(u);
    setSelectedPackage(null);
    // default validity: 1 month from today
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    setValidityDate(d.toISOString().split("T")[0]);
    setSaveSuccess(false);
  };

  // Auto-fill shake qty when package is selected
  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
  };

  // Users where hasPurchasedPackage is false or not set
  const noPackageUsers = useMemo(() => {
    return allUsers
      .filter((u) => {
        const hasPurchased = u.inventory?.hasPurchasedPackage;
        return (
          hasPurchased === false ||
          hasPurchased === undefined ||
          hasPurchased === null ||
          hasPurchased === ""
        );
      })
      .map((u) => {
        const sponsor = u.sponsoredBy || u.accountSecurity?.sponsoredBy || "No Sponsor";
        const referrer = u.accountSecurity?.referredBy || u.referredBy || "No Referrer";
        return {
          ...u,
          sponsor,
          referrer,
          totalCompQty: Number(u.inventory?.complementaryShakeQuantity) || 0,
          // Same field priority as UsersList.jsx & AdminRechargeList.jsx
          availableShakes:
            Number(u.shakeQuantity?.available) ||
            Number(u.inventory?.remainingShakes) ||
            Number(u.inventory?.complementaryShakeQuantity) ||
            0,
        };
      })
      .sort((a, b) => {
        const nameA = (
          a.personalInformation?.fullName ||
          a.personalDetails?.name ||
          a.name ||
          ""
        ).toLowerCase();
        const nameB = (
          b.personalInformation?.fullName ||
          b.personalDetails?.name ||
          b.name ||
          ""
        ).toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [allUsers]);

  // Dynamic list of unique referrers & sponsors (for dropdowns)
  const uniqueReferrers = useMemo(() => {
    const vals = [...new Set(noPackageUsers.map((u) => u.referrer).filter(Boolean))];
    return vals.sort((a, b) => a.localeCompare(b));
  }, [noPackageUsers]);

  const uniqueSponsors = useMemo(() => {
    const vals = [...new Set(noPackageUsers.map((u) => u.sponsor).filter(Boolean))];
    return vals.sort((a, b) => a.localeCompare(b));
  }, [noPackageUsers]);

  // Apply search and date filters
  const filteredUsers = useMemo(() => {
    return noPackageUsers.filter((u) => {
      // 1. Search filter
      let matchesSearch = true;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const name = (
          u.personalInformation?.fullName ||
          u.personalDetails?.name ||
          u.name ||
          ""
        ).toLowerCase();
        const mobile =
          u.personalInformation?.mobileNumber ||
          u.personalDetails?.mobile ||
          u.mobile ||
          "";
        matchesSearch = name.includes(term) || mobile.includes(term);
      }

      if (!matchesSearch) return false;

      // 2. Referrer filter
      if (selectedReferrer !== "All" && u.referrer !== selectedReferrer) {
        return false;
      }

      // 3. Sponsor filter
      if (selectedSponsor !== "All" && u.sponsor !== selectedSponsor) {
        return false;
      }

      // 4. Date filter (based on registered / joined date)
      if (dateFilter === "All") return true;

      const rawDate = u.createdAt || u.joinDate || u.metadata?.createdAt;
      if (!rawDate) return false;

      const joinDate = new Date(rawDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (dateFilter === "Today") {
        const startOfToday = new Date(today);
        return joinDate >= startOfToday;
      }

      if (dateFilter === "This Week") {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        return joinDate >= startOfWeek;
      }

      if (dateFilter === "This Month") {
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return joinDate >= startOfMonth;
      }

      if (dateFilter === "Custom") {
        let matchesDate = true;
        if (fromDate) {
          const start = new Date(fromDate);
          start.setHours(0, 0, 0, 0);
          matchesDate = matchesDate && joinDate >= start;
        }
        if (toDate) {
          const end = new Date(toDate);
          end.setHours(23, 59, 59, 999);
          matchesDate = matchesDate && joinDate <= end;
        }
        return matchesDate;
      }

      return true;
    });
  }, [noPackageUsers, searchTerm, selectedReferrer, selectedSponsor, dateFilter, fromDate, toDate]);

  // Grand totals
  const grandTotalCompQty = useMemo(
    () => noPackageUsers.reduce((sum, u) => sum + u.totalCompQty, 0),
    [noPackageUsers],
  );
  // Available qty = inventory.remainingShakes (same as SHAKES column in UsersList)
  const grandTotalAvailable = useMemo(
    () => noPackageUsers.reduce((sum, u) => sum + u.availableShakes, 0),
    [noPackageUsers],
  );

  const availableQty = (u) => u.availableShakes;

  const getJoinDate = (u) => {
    const raw = u.createdAt || u.joinDate || u.metadata?.createdAt;
    if (!raw) return "N/A";
    try {
      return new Date(raw).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    const exportData = filteredUsers.map((u, idx) => {
      const name =
        u.personalInformation?.fullName ||
        u.personalDetails?.name ||
        u.name ||
        "Unknown";
      const mobile =
        u.personalInformation?.mobileNumber ||
        u.personalDetails?.mobile ||
        u.mobile ||
        "N/A";
      const sponsor = u.sponsoredBy || u.accountSecurity?.sponsoredBy || "N/A";
      const referrer = u.accountSecurity?.referredBy || u.referredBy || "N/A";
      return {
        "S.No": idx + 1,
        Name: name,
        Mobile: mobile,
        "Joined On": getJoinDate(u),
        "Total Comp. Qty": u.totalCompQty,
        "Available Qty": availableQty(u),
        Sponsor: sponsor,
        Referrer: referrer,
      };
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "No Package Users");
    XLSX.writeFile(
      wb,
      `No_Package_Users_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
    toast.success("Exported to Excel");
    setShowExportMenu(false);
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("No Package Users Report", 14, 22);
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString("en-IN")}`, 14, 28);

    const tableData = filteredUsers.map((u, idx) => {
      const name =
        u.personalInformation?.fullName ||
        u.personalDetails?.name ||
        u.name ||
        "Unknown";
      const mobile =
        u.personalInformation?.mobileNumber ||
        u.personalDetails?.mobile ||
        u.mobile ||
        "N/A";
      const sponsor = u.sponsoredBy || u.accountSecurity?.sponsoredBy || "N/A";
      return [
        idx + 1,
        name,
        mobile,
        getJoinDate(u),
        u.totalCompQty,
        availableQty(u),
        sponsor,
      ];
    });

    autoTable(doc, {
      head: [
        [
          "S.No",
          "Name",
          "Mobile",
          "Joined On",
          "Comp. Qty",
          "Avail. Qty",
          "Sponsor",
        ],
      ],
      body: tableData,
      startY: 33,
      styles: { fontSize: 8.5 },
      headStyles: { fillColor: [15, 23, 42] },
    });

    doc.save(`No_Package_Users_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("Exported to PDF");
    setShowExportMenu(false);
  };

  // Consolidated Report Excel (Grouped by Sponsor)
  const exportConsolidatedExcel = () => {
    const wb = XLSX.utils.book_new();
    const rows = [];
    const cols = [
      "S.No",
      "Name",
      "Mobile",
      "Joined On",
      "Comp. Qty",
      "Available Qty",
    ];

    rows.push([
      `No Package Users Consolidated Report (Grouped by Sponsor)`,
      "",
      "",
      "",
      "",
      "",
    ]);
    rows.push([
      `Generated on: ${new Date().toLocaleDateString("en-IN")}`,
      "",
      "",
      "",
      "",
      "",
    ]);
    rows.push(["", "", "", "", "", ""]); // blank row

    // Column header row
    rows.push(cols);

    // Group by Sponsor
    const sponsorGroups = {};
    filteredUsers.forEach((u) => {
      const sponsor =
        u.sponsoredBy || u.accountSecurity?.sponsoredBy || "No Sponsor";
      if (!sponsorGroups[sponsor]) {
        sponsorGroups[sponsor] = [];
      }
      sponsorGroups[sponsor].push(u);
    });

    const sponsors = Object.keys(sponsorGroups).sort((a, b) =>
      a.localeCompare(b),
    );

    sponsors.forEach((sponsor) => {
      const groupUsers = sponsorGroups[sponsor];
      const groupCount = groupUsers.length;
      const groupTotalComp = groupUsers.reduce(
        (sum, u) => sum + u.totalCompQty,
        0,
      );
      const groupTotalAvail = groupUsers.reduce(
        (sum, u) => sum + availableQty(u),
        0,
      );

      // Dashed separator
      rows.push(["---", "---", "---", "---", "---", "---"]);
      // Group name row
      rows.push([`SPONSOR: ${sponsor.toUpperCase()}`, "", "", "", "", ""]);

      // Data rows
      groupUsers.forEach((u, idx) => {
        const name =
          u.personalInformation?.fullName ||
          u.personalDetails?.name ||
          u.name ||
          "Unknown";
        const mobile =
          u.personalInformation?.mobileNumber ||
          u.personalDetails?.mobile ||
          u.mobile ||
          "N/A";
        rows.push([
          idx + 1,
          name,
          mobile,
          getJoinDate(u),
          u.totalCompQty,
          availableQty(u),
        ]);
      });

      // Dashed separator
      rows.push(["---", "---", "---", "---", "---", "---"]);
      // Group footer
      rows.push([
        "Group Count:",
        groupCount,
        "Group Totals:",
        "",
        groupTotalComp,
        groupTotalAvail,
      ]);
    });

    // Overall grand total rows
    const grandCount = filteredUsers.length;
    const grandTotalComp = filteredUsers.reduce(
      (sum, u) => sum + u.totalCompQty,
      0,
    );
    const grandTotalAvail = filteredUsers.reduce(
      (sum, u) => sum + availableQty(u),
      0,
    );

    rows.push(["===", "===", "===", "===", "===", "==="]);
    rows.push([
      `Overall Total (${grandCount} users)`,
      "",
      "",
      "",
      grandTotalComp,
      grandTotalAvail,
    ]);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [
      { wch: 8 },
      { wch: 25 },
      { wch: 15 },
      { wch: 15 },
      { wch: 12 },
      { wch: 15 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, "Consolidated Report");
    XLSX.writeFile(
      wb,
      `No_Package_Consolidated_Report_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
    toast.success("Consolidated Excel Exported");
    setShowExportMenu(false);
  };

  // Consolidated Report PDF (Grouped by Sponsor)
  const exportConsolidatedPDF = () => {
    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(30, 30, 30);
    doc.text("No Package Users Consolidated Report", pageW / 2, 16, {
      align: "center",
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(80, 80, 80);
    doc.text(
      `Grouped by Sponsor | Generated on: ${new Date().toLocaleDateString("en-IN")}`,
      pageW / 2,
      22,
      { align: "center" },
    );

    // Thin horizontal rule below header
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.line(10, 26, pageW - 10, 26);

    // Build table body
    const sponsorGroups = {};
    filteredUsers.forEach((u) => {
      const sponsor =
        u.sponsoredBy || u.accountSecurity?.sponsoredBy || "No Sponsor";
      if (!sponsorGroups[sponsor]) {
        sponsorGroups[sponsor] = [];
      }
      sponsorGroups[sponsor].push(u);
    });

    const sponsors = Object.keys(sponsorGroups).sort((a, b) =>
      a.localeCompare(b),
    );
    const body = [];
    const RED = [180, 0, 0];
    const GRAY_LINE = [210, 210, 210];
    const NAVY = [15, 23, 42];

    sponsors.forEach((sponsor) => {
      const groupUsers = sponsorGroups[sponsor];
      const groupCount = groupUsers.length;
      const groupTotalComp = groupUsers.reduce(
        (sum, u) => sum + u.totalCompQty,
        0,
      );
      const groupTotalAvail = groupUsers.reduce(
        (sum, u) => sum + availableQty(u),
        0,
      );

      // Sponsor banner row (red bold, full span)
      body.push([
        {
          content: `SPONSOR: ${sponsor.toUpperCase()}`,
          colSpan: 6,
          styles: {
            fontStyle: "bold",
            textColor: RED,
            fillColor: [255, 248, 248],
            fontSize: 8.5,
            cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
          },
        },
      ]);

      // Data rows
      groupUsers.forEach((u, idx) => {
        const name =
          u.personalInformation?.fullName ||
          u.personalDetails?.name ||
          u.name ||
          "Unknown";
        const mobile =
          u.personalInformation?.mobileNumber ||
          u.personalDetails?.mobile ||
          u.mobile ||
          "N/A";
        body.push([
          idx + 1,
          name,
          mobile,
          getJoinDate(u),
          { content: String(u.totalCompQty), styles: { halign: "right" } },
          { content: String(availableQty(u)), styles: { halign: "right" } },
        ]);
      });

      // Group footer
      body.push([
        {
          content: `Group Count: ${groupCount}`,
          colSpan: 2,
          styles: { fontStyle: "bold", textColor: RED },
        },
        {
          content: "Group Totals:",
          colSpan: 2,
          styles: { fontStyle: "bold", textColor: RED },
        },
        {
          content: String(groupTotalComp),
          styles: { fontStyle: "bold", textColor: RED, halign: "right" },
        },
        {
          content: String(groupTotalAvail),
          styles: { fontStyle: "bold", textColor: RED, halign: "right" },
        },
      ]);

      // Thin spacer row between groups
      body.push([
        {
          content: "",
          colSpan: 6,
          styles: {
            fillColor: [240, 240, 240],
            minCellHeight: 2,
            cellPadding: 0,
          },
        },
      ]);
    });

    // Overall Grand Total row
    const grandCount = filteredUsers.length;
    const grandTotalComp = filteredUsers.reduce(
      (sum, u) => sum + u.totalCompQty,
      0,
    );
    const grandTotalAvail = filteredUsers.reduce(
      (sum, u) => sum + availableQty(u),
      0,
    );

    body.push([
      {
        content: "",
        colSpan: 6,
        styles: {
          fillColor: [210, 210, 210],
          minCellHeight: 1.5,
          cellPadding: 0,
        },
      },
    ]);
    body.push([
      {
        content: `Overall Total (${grandCount} users)`,
        colSpan: 4,
        styles: {
          fontStyle: "bold",
          textColor: [255, 255, 255],
          fillColor: NAVY,
          fontSize: 9,
          cellPadding: { top: 4, bottom: 4, left: 5, right: 3 },
        },
      },
      {
        content: String(grandTotalComp),
        styles: {
          fontStyle: "bold",
          textColor: [255, 255, 255],
          fillColor: NAVY,
          fontSize: 9,
          halign: "right",
          cellPadding: { top: 4, bottom: 4, right: 3 },
        },
      },
      {
        content: String(grandTotalAvail),
        styles: {
          fontStyle: "bold",
          textColor: [255, 255, 255],
          fillColor: NAVY,
          fontSize: 9,
          halign: "right",
          cellPadding: { top: 4, bottom: 4, right: 3 },
        },
      },
    ]);

    autoTable(doc, {
      head: [
        [
          "S.No",
          "Name",
          "Mobile",
          "Joined On",
          { content: "Comp. Qty", styles: { halign: "right" } },
          { content: "Avail. Qty", styles: { halign: "right" } },
        ],
      ],
      body: body,
      startY: 30,
      margin: { left: 10, right: 10 },
      theme: "plain",
      styles: {
        fontSize: 8,
        cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
        textColor: [30, 30, 30],
        lineColor: GRAY_LINE,
        lineWidth: 0,
      },
      headStyles: {
        fontStyle: "bold",
        textColor: [0, 0, 0],
        fillColor: [255, 255, 255],
        lineColor: [0, 0, 0],
        lineWidth: { bottom: 0.5 },
        cellPadding: { top: 4, bottom: 4, left: 3, right: 3 },
      },
      alternateRowStyles: { fillColor: [250, 250, 252] },
      columnStyles: {
        0: { cellWidth: 15 },
        1: { cellWidth: 50 },
        2: { cellWidth: 35 },
        3: { cellWidth: 35 },
        4: { cellWidth: 25, halign: "right" },
        5: { cellWidth: 30, halign: "right" },
      },
      didDrawPage: (data) => {
        const pg = doc.internal.getCurrentPageInfo().pageNumber;
        const total = doc.internal.getNumberOfPages();
        doc.setFontSize(7.5);
        doc.setTextColor(150);
        doc.text(
          `Page ${pg} of ${total}`,
          pageW - 10,
          doc.internal.pageSize.getHeight() - 7,
          { align: "right" },
        );
        doc.text(
          `Generated: ${new Date().toLocaleDateString("en-IN")}`,
          10,
          doc.internal.pageSize.getHeight() - 7,
        );
      },
    });

    doc.save(
      `No_Package_Consolidated_Report_${new Date().toISOString().split("T")[0]}.pdf`,
    );
    toast.success("Consolidated PDF Exported");
    setShowExportMenu(false);
  };

  const handlePrint = () => {
    window.print();
    setShowExportMenu(false);
  };

  // Save package update to Firestore
  const handleSavePackage = async () => {
    if (!editingUser || !selectedPackage) return;
    setIsSaving(true);
    try {
      const userRef = doc(db, "users", editingUser.id);

      await updateDoc(userRef, {
        "inventory.hasPurchasedPackage": true,
        "inventory.shakePackage": selectedPackage.name,
        "inventory.topUpPlan": selectedPackage.days,
        "inventory.packageValidityDate": validityDate,
        "inventory.packagePurchasedAt": new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      // Update local state — user disappears from this list
      setAllUsers((prev) =>
        prev.map((u) =>
          u.id === editingUser.id
            ? {
                ...u,
                inventory: {
                  ...u.inventory,
                  hasPurchasedPackage: true,
                  shakePackage: selectedPackage.name,
                  topUpPlan: selectedPackage.days,
                  packageValidityDate: validityDate,
                },
              }
            : u,
        ),
      );

      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
        setEditingUser(null);
      }, 1400);
    } catch (error) {
      console.error("Error updating package:", error);
      alert("Failed to update. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-100 p-4 md:p-6 lg:p-8 font-sans text-neutral-800">
      {/* ── Update Package Modal ── */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-neutral-200 overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100 bg-gradient-to-r from-green-50 to-emerald-50">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                  <Package className="w-4 h-4 text-green-700" />
                </div>
                <span className="font-bold text-neutral-900 text-sm">
                  Update Package
                </span>
              </div>
              <button
                onClick={() => setEditingUser(null)}
                className="w-7 h-7 rounded-full hover:bg-neutral-100 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-neutral-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-5 py-4 space-y-4 max-h-[80vh] overflow-y-auto">
              {/* User Info */}
              <div className="bg-neutral-50 rounded-xl p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-neutral-200 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-neutral-500" />
                </div>
                <div>
                  <p className="font-semibold text-neutral-900 text-sm">
                    {editingUser.personalInformation?.fullName ||
                      editingUser.personalDetails?.name ||
                      editingUser.name ||
                      "Unknown"}
                  </p>
                  <p className="text-xs text-neutral-500">
                    {editingUser.personalInformation?.mobileNumber ||
                      editingUser.personalDetails?.mobile ||
                      editingUser.mobile ||
                      "N/A"}
                  </p>
                </div>
                <div className="ml-auto flex gap-2">
                  <div className="text-center">
                    <p className="text-[10px] text-neutral-400">Comp. Qty</p>
                    <p className="text-sm font-extrabold text-purple-700">
                      {editingUser.totalCompQty}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-neutral-400">Available</p>
                    <p className="text-sm font-extrabold text-green-700">
                      {availableQty(editingUser)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Package Selection */}
              <div>
                <p className="text-xs font-bold text-neutral-600 uppercase tracking-wide mb-2">
                  Select Package
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {SHAKE_PACKAGES.map((pkg) => (
                    <button
                      key={pkg.name}
                      onClick={() => handlePackageSelect(pkg)}
                      className={`rounded-xl border-2 p-3 text-left transition-all ${
                        selectedPackage?.name === pkg.name
                          ? "border-green-500 bg-green-50"
                          : "border-neutral-200 bg-white hover:border-neutral-300"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span
                          className={`text-xs font-bold ${selectedPackage?.name === pkg.name ? "text-green-700" : "text-neutral-700"}`}
                        >
                          {pkg.name}
                        </span>
                        {selectedPackage?.name === pkg.name && (
                          <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                        )}
                      </div>
                      <p className="text-[11px] text-neutral-500">
                        {pkg.days} day{pkg.days > 1 ? "s" : ""}
                      </p>
                      <p className="text-xs font-semibold text-neutral-800 mt-0.5">
                        ₹{pkg.totalPrice.toLocaleString()}
                        <span className="text-[10px] text-neutral-400 ml-1">
                          (₹{pkg.pricePerDay}/d)
                        </span>
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Validity Date */}
              <div>
                <label className="text-xs font-bold text-neutral-600 uppercase tracking-wide mb-1.5 block flex items-center gap-1">
                  <CalendarDays className="w-3 h-3" />
                  Package Valid Until
                </label>
                <input
                  type="date"
                  value={validityDate}
                  onChange={(e) => setValidityDate(e.target.value)}
                  className="w-full px-3 py-2.5 border border-neutral-200 rounded-xl text-sm focus:ring-2 focus:ring-green-400 focus:border-green-400 outline-none"
                />
              </div>

              {/* Status Preview */}
              {selectedPackage && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                  <p className="text-[11px] text-neutral-500 font-medium mb-2">
                    Summary
                  </p>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-neutral-600">Package:</span>
                    <span className="font-bold text-green-700">
                      {selectedPackage.name}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs mt-1">
                    <span className="text-neutral-600">Valid until:</span>
                    <span className="font-bold text-neutral-800">
                      {validityDate
                        ? new Date(validityDate).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "—"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs mt-1">
                    <span className="text-neutral-600">Status after save:</span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-bold text-[11px]">
                      <CheckCircle className="w-3 h-3" />
                      Package Purchased
                    </span>
                  </div>
                </div>
              )}

              <p className="text-[11px] text-neutral-400 text-center">
                Saving will mark the user as a paid subscriber and move them to
                the Paid Subscribers list.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="px-5 pb-5 flex gap-2 border-t border-neutral-100 pt-4">
              <button
                onClick={() => setEditingUser(null)}
                disabled={isSaving}
                className="flex-1 py-2.5 rounded-xl border border-neutral-200 text-sm font-semibold text-neutral-600 hover:bg-neutral-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePackage}
                disabled={isSaving || saveSuccess || !selectedPackage}
                className="flex-1 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-bold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {saveSuccess ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Saved!
                  </>
                ) : isSaving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Package className="w-4 h-4" />
                    Update Package
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-2xl border border-neutral-200/60 shadow-sm p-4 md:p-6 mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-neutral-900 tracking-tight">
                No Package Users
              </h1>
              <p className="text-xs md:text-sm text-neutral-500 mt-0.5">
                Users who have not purchased any package yet
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadData}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-neutral-900 hover:bg-neutral-800 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-60"
            >
              <RefreshCw
                className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-neutral-300 text-neutral-700 rounded-xl hover:bg-neutral-50 transition-colors font-medium text-sm"
              >
                <FileDown className="w-4 h-4" />
                <span>Export</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-52 bg-white border border-neutral-200 rounded-xl shadow-lg z-50 py-1">
                  <button
                    onClick={exportToExcel}
                    className="w-full text-left px-4 py-2 hover:bg-neutral-50 text-sm text-neutral-700 font-medium"
                  >
                    Export to Excel
                  </button>
                  <button
                    onClick={exportToPDF}
                    className="w-full text-left px-4 py-2 hover:bg-neutral-50 text-sm text-neutral-700 font-medium"
                  >
                    Export to PDF
                  </button>
                  <button
                    onClick={exportConsolidatedExcel}
                    className="w-full text-left px-4 py-2 hover:bg-neutral-50 text-sm text-neutral-700 font-medium"
                  >
                    Consolidated Report (Excel)
                  </button>
                  <button
                    onClick={exportConsolidatedPDF}
                    className="w-full text-left px-4 py-2 hover:bg-neutral-50 text-sm text-neutral-700 font-medium"
                  >
                    Consolidated Report (PDF)
                  </button>
                  <button
                    onClick={handlePrint}
                    className="w-full text-left px-4 py-2 hover:bg-neutral-50 text-sm text-neutral-700 font-medium"
                  >
                    Print
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="bg-orange-50 rounded-xl p-4 border border-white shadow-sm">
          <p className="text-[11px] text-neutral-500 font-medium">
            Total Users
          </p>
          <p className="text-2xl font-extrabold mt-1 text-orange-600">
            {isLoading ? "..." : noPackageUsers.length}
          </p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 border border-white shadow-sm">
          <p className="text-[11px] text-neutral-500 font-medium">
            Showing (Filtered)
          </p>
          <p className="text-2xl font-extrabold mt-1 text-blue-700">
            {isLoading ? "..." : filteredUsers.length}
          </p>
        </div>
        <div className="bg-purple-50 rounded-xl p-4 border border-white shadow-sm">
          <p className="text-[11px] text-neutral-500 font-medium">
            Total Comp. Qty
          </p>
          <p className="text-2xl font-extrabold mt-1 text-purple-700">
            {isLoading ? "..." : grandTotalCompQty}
          </p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 border border-white shadow-sm">
          <p className="text-[11px] text-neutral-500 font-medium">
            Total Available Qty
          </p>
          <p className="text-2xl font-extrabold mt-1 text-green-700">
            {isLoading ? "..." : grandTotalAvailable}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-neutral-200/60 shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 items-end">
          {/* Search */}
          <div className="md:col-span-1">
            <label className="block text-[11px] font-bold text-neutral-600 uppercase tracking-wide mb-1.5">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or mobile..."
                className="w-full pl-9 pr-4 py-2 border border-neutral-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
              />
            </div>
          </div>

          {/* Referred By */}
          <div>
            <label className="block text-[11px] font-bold text-neutral-600 uppercase tracking-wide mb-1.5">
              Referred By
            </label>
            <select
              value={selectedReferrer}
              onChange={(e) => setSelectedReferrer(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none bg-white font-semibold text-neutral-700"
            >
              <option value="All">All Referrers</option>
              {uniqueReferrers.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          {/* Sponsored By */}
          <div>
            <label className="block text-[11px] font-bold text-neutral-600 uppercase tracking-wide mb-1.5">
              Sponsored By
            </label>
            <select
              value={selectedSponsor}
              onChange={(e) => setSelectedSponsor(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none bg-white font-semibold text-neutral-700"
            >
              <option value="All">All Sponsors</option>
              {uniqueSponsors.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          {/* Date Filter */}
          <div>
            <label className="block text-[11px] font-bold text-neutral-600 uppercase tracking-wide mb-1.5">
              Joined Date Filter
            </label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none bg-white font-semibold text-neutral-700"
            >
              <option value="All">All Time</option>
              <option value="Today">Joined Today</option>
              <option value="This Week">Joined This Week</option>
              <option value="This Month">Joined This Month</option>
              <option value="Custom">Custom Range</option>
            </select>
          </div>

          {/* Custom Date Picker Inputs */}
          {dateFilter === "Custom" && (
            <>
              <div>
                <label className="block text-[11px] font-bold text-neutral-600 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                  <CalendarDays className="w-3.5 h-3.5 text-neutral-400" />
                  From Date
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none font-semibold text-neutral-700"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-neutral-600 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                  <CalendarDays className="w-3.5 h-3.5 text-neutral-400" />
                  To Date
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none font-semibold text-neutral-700"
                />
              </div>
            </>
          )}

          {/* Clear Filters Button */}
          {(searchTerm !== "" ||
            selectedReferrer !== "All" ||
            selectedSponsor !== "All" ||
            dateFilter !== "All" ||
            fromDate !== "" ||
            toDate !== "") && (
            <div>
              <button
                onClick={() => {
                  setSearchTerm("");
                  setSelectedReferrer("All");
                  setSelectedSponsor("All");
                  setDateFilter("All");
                  setFromDate("");
                  setToDate("");
                }}
                className="w-full py-2 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 font-semibold rounded-xl text-sm transition-colors border border-neutral-200"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-neutral-200/60 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <RefreshCw className="w-8 h-8 text-neutral-300 animate-spin" />
            <p className="text-sm text-neutral-400">Loading...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <ShoppingBag className="w-12 h-12 text-neutral-200" />
            <p className="text-sm text-neutral-400 font-medium">
              {searchTerm
                ? "No users match your search"
                : "All users have purchased a package"}
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-neutral-50 border-b border-neutral-100">
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-neutral-500 uppercase tracking-wide">
                      #
                    </th>
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-neutral-500 uppercase tracking-wide">
                      Name
                    </th>
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-neutral-500 uppercase tracking-wide">
                      Mobile
                    </th>
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-neutral-500 uppercase tracking-wide">
                      Joined On
                    </th>
                    <th className="text-center px-4 py-3 text-[11px] font-bold text-neutral-500 uppercase tracking-wide">
                      🍵 Total Comp. Qty
                    </th>
                    <th className="text-center px-4 py-3 text-[11px] font-bold text-neutral-500 uppercase tracking-wide">
                      ✅ Available Qty
                    </th>
                    <th className="text-center px-4 py-3 text-[11px] font-bold text-neutral-500 uppercase tracking-wide">
                      Package Status
                    </th>
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-neutral-500 uppercase tracking-wide">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-50">
                  {filteredUsers.map((u, idx) => {
                    const name =
                      u.personalInformation?.fullName ||
                      u.personalDetails?.name ||
                      u.name ||
                      "Unknown";
                    const mobile =
                      u.personalInformation?.mobileNumber ||
                      u.personalDetails?.mobile ||
                      u.mobile ||
                      "N/A";
                    const avail = availableQty(u);
                    return (
                      <tr
                        key={u.id}
                        className="hover:bg-neutral-50/70 transition-colors"
                      >
                        <td className="px-4 py-3.5 text-neutral-400 font-medium text-xs">
                          {idx + 1}
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0">
                              <User className="w-3.5 h-3.5 text-neutral-500" />
                            </div>
                            <span className="font-semibold text-neutral-900">
                              {name}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-1.5 text-neutral-600">
                            <Phone className="w-3 h-3 text-neutral-400" />
                            {mobile}
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-neutral-500 text-xs">
                          {getJoinDate(u)}
                        </td>
                        {/* Total Comp. Qty */}
                        <td className="px-4 py-3.5 text-center">
                          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-purple-100 text-purple-700 font-extrabold text-sm">
                            {u.totalCompQty}
                          </span>
                        </td>
                        {/* Available Qty */}
                        <td className="px-4 py-3.5 text-center">
                          <span
                            className={`inline-flex items-center justify-center w-9 h-9 rounded-full font-extrabold text-sm ${
                              avail === 0
                                ? "bg-red-100 text-red-600"
                                : avail <= 1
                                  ? "bg-orange-100 text-orange-600"
                                  : "bg-green-100 text-green-700"
                            }`}
                          >
                            {avail}
                          </span>
                        </td>
                        {/* Package Status — clickable to open modal */}
                        <td className="px-4 py-3.5 text-center">
                          <button
                            onClick={() => openEditModal(u)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors group"
                          >
                            <ShoppingBag className="w-3 h-3" />
                            No Package
                            <Pencil className="w-3 h-3 opacity-60 group-hover:opacity-100" />
                          </button>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2">
                            {/* Update Package — primary CTA */}
                            <button
                              onClick={() => openEditModal(u)}
                              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                            >
                              <Package className="w-3 h-3" />
                              Pkg
                            </button>
                            <button
                              onClick={() =>
                                navigate(`/admin/edit-user/${u.id}`)
                              }
                              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-neutral-800 hover:bg-neutral-700 rounded-lg transition-colors"
                            >
                              <Pencil className="w-3 h-3" />
                              Edit
                            </button>
                            <button
                              onClick={() =>
                                navigate(`/admin/user-details/${u.id}`)
                              }
                              className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                            >
                              View
                              <ExternalLink className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-neutral-50 border-t-2 border-neutral-200">
                    <td className="px-4 py-3" colSpan={4}>
                      <span className="text-xs font-bold text-neutral-600 uppercase tracking-wide">
                        Grand Total — {filteredUsers.length} user
                        {filteredUsers.length !== 1 ? "s" : ""}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-extrabold text-purple-700">
                        {filteredUsers.reduce((s, u) => s + u.totalCompQty, 0)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-extrabold text-green-700">
                        {filteredUsers.reduce((s, u) => s + availableQty(u), 0)}
                      </span>
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Mobile Card List */}
            <div className="md:hidden divide-y divide-neutral-100">
              {filteredUsers.map((u) => {
                const name =
                  u.personalInformation?.fullName ||
                  u.personalDetails?.name ||
                  u.name ||
                  "Unknown";
                const mobile =
                  u.personalInformation?.mobileNumber ||
                  u.personalDetails?.mobile ||
                  u.mobile ||
                  "N/A";
                const avail = availableQty(u);
                return (
                  <div key={u.id} className="p-4 hover:bg-neutral-50">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <p className="font-semibold text-neutral-900">
                            {name}
                          </p>
                          <button
                            onClick={() => openEditModal(u)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors"
                          >
                            <ShoppingBag className="w-3 h-3" />
                            No Package
                            <Pencil className="w-2.5 h-2.5 opacity-70" />
                          </button>
                        </div>
                        <p className="text-xs text-neutral-500 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {mobile}
                        </p>
                        <p className="text-xs text-neutral-400 mt-1">
                          Joined: {getJoinDate(u)}
                        </p>
                        <div className="mt-2 flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-neutral-500">
                              🍵 Total:
                            </span>
                            <span className="text-sm font-extrabold text-purple-700">
                              {u.totalCompQty}
                            </span>
                          </div>
                          <div className="w-px h-4 bg-neutral-200" />
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-neutral-500">
                              ✅ Avail:
                            </span>
                            <span
                              className={`text-sm font-extrabold ${
                                avail === 0
                                  ? "text-red-600"
                                  : avail <= 1
                                    ? "text-orange-600"
                                    : "text-green-700"
                              }`}
                            >
                              {avail}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        {/* Update Package — primary button */}
                        <button
                          onClick={() => openEditModal(u)}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                        >
                          <Package className="w-3 h-3" />
                          Update Pkg
                        </button>
                        <button
                          onClick={() => navigate(`/admin/edit-user/${u.id}`)}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-neutral-800 hover:bg-neutral-700 rounded-lg transition-colors"
                        >
                          <Pencil className="w-3 h-3" />
                          Edit
                        </button>
                        <button
                          onClick={() =>
                            navigate(`/admin/user-details/${u.id}`)
                          }
                          className="text-xs text-blue-600 font-semibold flex items-center gap-1"
                        >
                          View <ExternalLink className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {/* Mobile Footer */}
              <div className="bg-neutral-50 px-4 py-3 border-t-2 border-neutral-200 flex justify-between items-center">
                <span className="text-xs font-bold text-neutral-600 uppercase">
                  Total Users
                </span>
                <span className="text-lg font-extrabold text-orange-600">
                  {filteredUsers.length}
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      {!isLoading && filteredUsers.length > 0 && (
        <p className="text-center text-xs text-neutral-400 mt-4">
          Showing {filteredUsers.length} user
          {filteredUsers.length > 1 ? "s" : ""} who have not purchased a package
        </p>
      )}
    </div>
  );
};

export default NoTransactionList;
